<template>
  <div class="bg-white flex justify-center">
    <div class="container">
      <ClientOnly>
        <div v-if="loading" class="pt-10 pb-20 px-10 text-center">
          <div
            class="loading-container"
            :class="{ 'mobile-optimized': isMobileDevice }"
          >
            <div class="onepass-logo" v-if="!isMobileDevice">
              <img
                src="/assets/images/onepass/logos/onepass-mobile-primary-logo.svg"
                alt="OnePass Mobile"
                width="180"
              />
            </div>
            <div class="spinner"></div>
            <p class="loading-message">{{ loadingMessage }}</p>
            <p class="text-sm text-gray-500 mt-2">
              <span v-if="blocks.length === 0"
                >Connecting to content service...</span
              >
              <span v-else>Preparing your OnePass experience...</span>
            </p>
            <!-- Loading progress bar -->
            <div class="loading-progress-container mt-4">
              <div
                class="loading-progress-bar"
                :style="{
                  width:
                    typeof loadingProgress === 'number'
                      ? `${loadingProgress}%`
                      : '0%',
                }"
              ></div>
            </div>
            <p class="text-xs text-gray-400 mt-1">
              {{ typeof loadingProgress === "number" ? loadingProgress : 0 }}%
              complete
            </p>
          </div>
        </div>

        <div v-else-if="error" class="pt-2 pb-10 px-10 text-center">
          <h2 class="text-xl font-bold text-red-500 mb-2">
            Unable to load content
          </h2>
          <p class="text-gray-500">Please try again later or contact support</p>
        </div>

        <DynamicZone
          v-else-if="page && blocks && blocks.length > 0"
          :blocks="blocks"
          :key="`dynamic-zone-${blocks.length}`"
        />

        <div v-else class="pt-2 pb-10 px-10 text-center">
          <h2 class="text-xl font-bold mb-2">Welcome to GO Commerce</h2>
          <p class="text-gray-500">
            No content has been configured for the homepage.
          </p>
          <div
            class="mt-4 p-4 bg-gray-100 text-left rounded text-xs"
            style="max-width: 600px; margin: 0 auto"
          >
            <p class="font-bold">Debug Information:</p>
            <p>Page Value: {{ page ? "Exists" : "Not Found" }}</p>
            <p>Loading: {{ loading }}</p>
            <p>Error: {{ error }}</p>
            <p>Blocks: {{ blocks?.length || 0 }}</p>
          </div>
        </div>
      </ClientOnly>
    </div>
  </div>
</template>

<script setup lang="ts">
// Import comprehensive tracking composables for Product Discovery
import { trackEvent } from '~/utils/posthogTracking';
import { usePostHogTracking } from '~/composables/usePostHogTracking';

const strapi = useStrapiService();
const page = ref<any>(null);
const blocks = ref<any[]>([]);
const loading = ref(true); // Set to true by default to show spinner immediately
const error = ref(false);
const loadingProgress = ref(0);
const loadingMessage = ref("Connecting to OnePass Mobile...");
const isMobileDevice = ref(false);

// Initialize Product Discovery tracking (Week 1, Day 3-5)
const { 
  product, 
  startTimer, 
  endTimer, 
  trackComponentError
} = usePostHogTracking('HomepageIndex');

useHead({
  title: "OnePass mobile | 365 Day Mobile Plans, Prepaid Plans and more",
});

// Ensure loading state is applied before any rendering
if (process.client) {
  loading.value = true;

  // Check if user is on a mobile device for UI optimization
  const checkMobile = () => {
    isMobileDevice.value =
      window.innerWidth <= 767 ||
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );
  };

  // Initial check and add event listener for resize
  checkMobile();
  window.addEventListener("resize", checkMobile);

  // Clean up event listener on component unmount
  onBeforeUnmount(() => {
    window.removeEventListener("resize", checkMobile);
  });
} else {
  onBeforeMount(() => {
    loading.value = true;
  });
}

// Log important initialization info without exposing sensitive data
console.log("===== HOME PAGE INITIALIZATION =====");
console.log("Environment mode:", process.env.NODE_ENV);
console.log("Client initialized:", process.client ? "Yes" : "No");

// Use onMounted to ensure client-side fetching if SSR fails
onMounted(async () => {
  // Start homepage performance tracking (Week 1 metrics)
  startTimer('homepage_total_load');
  startTimer('homepage_content_fetch');
  
  // Declare attempts variable at function scope
  let attempts = 0;

  // Track homepage visit with enhanced discovery context
  try {
    trackEvent('page_viewed', {
      page_type: 'landing',
      page_name: 'homepage',
      content_blocks: blocks.value?.length || 0,
      is_mobile: isMobileDevice.value,
      loading_start_time: Date.now(),
      // Discovery tracking context
      session_type: 'product_discovery',
      entry_point: 'direct_homepage'
    }, 'HomepageIndex');
    console.log('[PostHog] Homepage discovery session started');
  } catch (error) {
    console.error('[PostHog] Failed to track homepage view:', error);
    // Track tracking failure itself
    trackComponentError('homepage_tracking_failed', error as Error, {
      tracking_type: 'page_view'
    });
  }

  // Make sure loading is visible immediately - reinforcement
  loading.value = true;

  // Force a layout reflow to ensure spinner is visible
  await nextTick();

  // Increased minimum loading time with visual feedback
  const minLoadingTime = 1500; // 1.5 seconds for better visibility
  const startTime = Date.now();
  // Reset and simulate loading progress with more realistic progression
  loadingProgress.value = 0; // Reset progress
  loadingMessage.value = "Connecting to OnePass Mobile...";

  // Progressive loading messages to make experience feel more dynamic
  const loadingMessages = [
    { progress: 0, message: "Connecting to OnePass Mobile..." },
    { progress: 20, message: "Retrieving your OnePass data..." },
    { progress: 40, message: "Loading mobile plans..." },
    { progress: 60, message: "Preparing content for you..." },
    { progress: 80, message: "Almost there..." },
  ];

  const loadingInterval = setInterval(() => {
    if (loadingProgress.value < 90) {
      // More controlled progression - smoother increments
      loadingProgress.value += Math.floor(Math.random() * 5) + 2;

      // Update message based on progress
      for (const msgData of loadingMessages) {
        if (loadingProgress.value >= msgData.progress) {
          loadingMessage.value = msgData.message;
        }
      }
    }
  }, 200);

  try {
    console.log("Attempting to fetch home page content...");

    // Make multiple attempts to fetch content with increasing delays
    const maxAttempts = 3;

    while (attempts < maxAttempts) {
      console.log(
        `Fetch attempt ${attempts + 1}/${maxAttempts} for home page...`
      );

      try {
        page.value = await strapi.getPageBySlugGraphql("home");

        if (page.value) {
          blocks.value = page.value.components || [];
          console.log(
            "Client-side page data received:",
            page.value ? "Yes" : "No"
          );
          console.log("Blocks count:", blocks.value.length);
          
          // Track successful content loading with discovery metrics (Week 1)
          const contentFetchTime = endTimer('homepage_content_fetch', false);
          
          try {
            // Track product discovery content success
            product.discovery({
              content_sections: blocks.value.length,
              load_time_ms: contentFetchTime
            });
            
            // Track additional discovery details
            trackEvent('content_discovery_loaded', {
              fetch_time_ms: contentFetchTime,
              attempt_number: attempts + 1,
              total_blocks: blocks.value.length,
              content_types: blocks.value.map((block: any) => block.__component || 'unknown'),
              is_mobile: isMobileDevice.value
            }, 'HomepageIndex');
            
            console.log('[PostHog] Product discovery content loaded successfully');
          } catch (trackingErr) {
            console.error('[PostHog] Failed to track discovery content loading:', trackingErr);
          }
          
          // If successful, exit the loop
          break;
        } else {
          console.warn(`Attempt ${attempts + 1} returned no page data`);
        }
      } catch (fetchErr) {
        console.error(`Error in fetch attempt ${attempts + 1}:`, fetchErr);
        
        // Track content fetch error
        trackComponentError('content_fetch_failed', fetchErr as Error, {
          attempt_number: attempts + 1,
          max_attempts: maxAttempts
        });
      }

      attempts++;
      if (attempts < maxAttempts) {
        // Exponential backoff - wait longer between subsequent attempts
        const delay = Math.pow(2, attempts) * 500;
        console.log(`Waiting ${delay}ms before next attempt...`);
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }

    if (!page.value || !blocks.value.length) {
      console.error("After all attempts, failed to load content");
      error.value = true;
      
      // Track final content loading failure
      trackComponentError('homepage_content_failed', new Error('Failed to load after all attempts'), {
        total_attempts: maxAttempts,
        final_state: 'no_content'
      });
    }
  } catch (err) {
    console.error("Unhandled error in client-side setup:", err);
    error.value = true;
    
    // Track unhandled homepage error
    trackComponentError('homepage_unhandled_error', err as Error, {
      context: 'client_side_setup'
    });
  } finally {
    // Complete homepage performance tracking
    const totalLoadTime = endTimer('homepage_total_load', false);
    
    // Track final homepage performance metrics
    try {
      trackEvent('performance_homepage_complete', {
        total_load_time_ms: totalLoadTime,
        content_blocks_loaded: blocks.value?.length || 0,
        has_error: error.value,
        is_mobile: isMobileDevice.value,
        loading_attempts: attempts || 1
      }, 'HomepageIndex');
      
      console.log(`[PostHog] Homepage loaded in ${totalLoadTime}ms with ${blocks.value?.length || 0} blocks`);
    } catch (trackingErr) {
      console.error('[PostHog] Failed to track homepage performance:', trackingErr);
    }

    // Ensure minimum loading time for smoother UX
    const elapsed = Date.now() - startTime;
    if (elapsed < minLoadingTime) {
      setTimeout(() => {
        // Complete the progress bar first
        loadingProgress.value = 100;
        setTimeout(() => {
          clearInterval(loadingInterval);
          loading.value = false;
        }, 300); // Short delay after progress reaches 100%
      }, minLoadingTime - elapsed);
    } else {
      // Complete the progress bar
      loadingProgress.value = 100;
      setTimeout(() => {
        clearInterval(loadingInterval);
        loading.value = false;
      }, 300);
    }
  }
});

// Server-side fetch
try {
  console.log("Attempting to fetch home page content...");
  page.value = await strapi.getPageBySlugGraphql("home").catch((err) => {
    console.error("GraphQL query error:", err);
    error.value = true;
    return null;
  });

  console.log("Server-side page data received:", page.value ? "Yes" : "No");

  if (page.value) {
    blocks.value = page.value.components || [];
    console.log("Server-side blocks count:", blocks.value.length);
  }
} catch (err) {
  console.error("Unhandled error in server-side setup:", err);
  error.value = true;
} finally {
  loading.value = false;
}
</script>

<style scoped>
.spinner-container {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 20px auto;
}

.spinner {
  width: 60px;
  height: 60px;
  border: 5px solid rgba(153, 0, 241, 0.2); /* OnePass purple with opacity */
  border-top: 5px solid #9900f1; /* OnePass purple */
  border-radius: 50%;
  animation: spin 1.2s linear infinite;
  z-index: 100;
  margin: 0 auto;
}

.loading-message {
  color: #9900f1;
  font-weight: 500;
  margin-top: 1rem;
}

.loading-progress-container {
  width: 80%;
  max-width: 300px;
  height: 6px; /* Slightly thicker for better visibility */
  background-color: rgba(153, 0, 241, 0.2);
  border-radius: 4px;
  margin: 0 auto;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.loading-progress-bar {
  height: 100%;
  background-color: #9900f1; /* OnePass brand color */
  border-radius: 4px;
  transition: width 0.3s ease-in-out;
  background-image: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.1) 25%,
    rgba(255, 255, 255, 0.2) 50%,
    rgba(255, 255, 255, 0.1) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite linear;
}

@keyframes shimmer {
  from {
    background-position: 200% 0;
  }
  to {
    background-position: 0 0;
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* Mobile responsiveness improvements */
@media (max-width: 768px) {
  .loading-container {
    padding: 0 20px;

    &.mobile-optimized {
      padding: 0 15px;
      max-width: 100%;

      .loading-message {
        font-size: 16px;
      }

      .spinner {
        width: 50px;
        height: 50px;
        border-width: 4px;
      }
    }

    .onepass-logo {
      margin-bottom: 20px;
    }
  }

  .loading-progress-container {
    width: 90%;
  }
}
</style>
